import React, { useRef, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { CategorySelector } from 'src/components/molecules';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import useRequest from 'src/utils/hooks/useRequest';

const BulkListingImport = () => {
  const { marketplace } = useMarketplaceContext();
  const [categoryId, setCategoryId] = useState<number>();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { api } = useRequest();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref for the file input

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setCsvFile(file);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!marketplace) {
      return;
    }

    if (!categoryId) {
      toast({
        title: 'Please select a category.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!csvFile) {
      toast({
        title: 'Please upload a CSV file.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('category_id', categoryId.toString());
      formData.append('file', csvFile);
      await api.marketplace.listings.bulkImport(marketplace.id, formData);

      toast({
        title: 'Success',
        description:
          'The import process has started. Listings imported will be available shortly in Listing Approvals section.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setCsvFile(null);
      setCategoryId(undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset the file input element
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error on CSV format, please check the file and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex direction="column" width="100%" position="relative">
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        width="100%"
        gap={2}
        mb={4}
      >
        <Text fontSize="xl" mb={4}>
          Bulk Listing Import
        </Text>
      </Flex>
      <form
        style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        onSubmit={handleSubmit}
      >
        <Stack width="fit-content">
          <FormControl id="category_id" isRequired>
            <FormLabel>Category</FormLabel>
            <CategorySelector
              mode="category-admin"
              marketplaceId={marketplace?.id}
              onCategorySelect={(category) => setCategoryId(category?.id)}
              selectedCategoryId={categoryId ? categoryId.toString() : undefined}
            />
          </FormControl>
        </Stack>
        <Stack>
          <FormControl id="file">
            <FormLabel>CSV File</FormLabel>
            <Input
              type="file"
              id="file"
              accept=".csv"
              onChange={handleFileChange}
              ref={fileInputRef} // Attach the ref to the file input
              style={{ display: 'none' }}
            />
            {csvFile && <Text mb={2}>{csvFile.name}</Text>}
            <label htmlFor="file">
              <Button as="span" variant="outline" size="sm">
                Upload CSV
              </Button>
            </label>
          </FormControl>
        </Stack>
        <Flex direction="row" alignItems="center" mb={4}>
          <Flex direction="row" alignItems="center" gap={4} justifyContent="flex-end" flexGrow="1">
            <Button colorScheme="primary" type="submit" isLoading={isLoading} disabled={isLoading}>
              Submit
            </Button>
          </Flex>
        </Flex>
      </form>
    </Flex>
  );
};

export default BulkListingImport;
